import { Web3Provider } from '@ethersproject/providers'
import { EthereumProvider } from '@walletconnect/ethereum-provider'
import { ethers } from 'ethers'

import { CONFIG, CROSSCHAIN_CONFIG } from '../config'

import { WalletType } from './types'
import { ChainConfig } from '~/types/config'

export const hasXDEFIExtension = (): boolean =>
	!!(window['xfi'] && window['xfi']?.ethereum)

export const hasCoin98Extension = (): boolean =>
	!!(window['coin98'] && window['coin98']?.provider)

export const hasMetamaskExtension = (): boolean =>
	!!(window['ethereum'] && window['ethereum']?.isMetaMask)

export const hasTrustExtension = (): boolean =>
	!!(window['ethereum'] && window['ethereum']?.isTrust)

export const hasImTokenExtension = (): boolean =>
	!!(
		(window['ethereum'] && window['ethereum']?.isImToken) ||
		window['imToken']
	)

export const hasBitKeepExtension = (): boolean =>
	!!(
		(window['bitkeep'] && window['bitkeep']?.ethereum) ||
		window['isBitKeep']
	)

export const hasOntoExtension = (): boolean =>
	!!(window['ethereum'] && window['ethereum']?.isONTO)

export const hasOperaExtension = (): boolean =>
	!!(window['ethereum'] && window['ethereum']?.isOpera)

export const hasSafePalExtension = (): boolean =>
	!!(window['ethereum'] && window['ethereum']?.isSafePal)

export const hasKuCoinExtension = (): boolean =>
	!!(window['ethereum'] && window['ethereum']?.isKuCoinWallet)

const getXDEFIProvider = (): Promise<Web3Provider> => {
	if (hasXDEFIExtension()) {
		return Promise.resolve(
			new ethers.providers.Web3Provider(window['xfi'].ethereum, 'any')
		)
	}
	return Promise.reject(new Error('No XDEFI Ethereum provider'))
}

const getCoin98Provider = (): Promise<Web3Provider> => {
	if (hasCoin98Extension()) {
		return Promise.resolve(
			new ethers.providers.Web3Provider(window['coin98'].provider, 'any')
		)
	}
	return Promise.reject(new Error('No Coin98 Ethereum provider'))
}

const getMetamaskProvider = (): Promise<Web3Provider> => {
	if (hasMetamaskExtension()) {
		return Promise.resolve(
			new ethers.providers.Web3Provider(window['ethereum'], 'any')
		)
	}
	return Promise.reject(new Error('No MetaMask Ethereum provider'))
}

const getTrustProvider = (): Promise<Web3Provider> => {
	if (hasTrustExtension()) {
		return Promise.resolve(
			new ethers.providers.Web3Provider(window['ethereum'], 'any')
		)
	}
	return Promise.reject(new Error('No WalletTrust Ethereum provider'))
}

const getBitKeepProvider = (): Promise<Web3Provider> => {
	if (hasBitKeepExtension()) {
		return Promise.resolve(
			new ethers.providers.Web3Provider(
				window['bitkeep']['ethereum'],
				'any'
			)
		)
	}
	return Promise.reject(new Error('No BitKeep Ethereum provider'))
}

const getOperaProvider = (): Promise<Web3Provider> => {
	if (hasBitKeepExtension()) {
		return Promise.resolve(
			new ethers.providers.Web3Provider(window['ethereum'], 'any')
		)
	}
	return Promise.reject(new Error('No Opera Ethereum provider'))
}

const getSafePalProvider = (): Promise<Web3Provider> => {
	if (hasSafePalExtension()) {
		return Promise.resolve(
			new ethers.providers.Web3Provider(window['ethereum'], 'any')
		)
	}
	return Promise.reject(new Error('No SafePal Ethereum provider'))
}

const getKuCoinProvider = (): Promise<Web3Provider> => {
	if (hasKuCoinExtension()) {
		return Promise.resolve(
			new ethers.providers.Web3Provider(window['ethereum'], 'any')
		)
	}
	return Promise.reject(new Error('No KuCoin Ethereum provider'))
}

const getImTokenProvider = (): Promise<Web3Provider> => {
	if (hasImTokenExtension()) {
		return Promise.resolve(
			new ethers.providers.Web3Provider(window['ethereum'], 'any')
		)
	}
	return Promise.reject(new Error('No imToken Ethereum provider'))
}

const getOntoProvider = (): Promise<Web3Provider> => {
	if (hasOntoExtension()) {
		return Promise.resolve(
			new ethers.providers.Web3Provider(window['ethereum'], 'any')
		)
	}
	return Promise.reject(new Error('No Onto Ethereum provider'))
}

const getWalletConnectProvider = async (): Promise<Web3Provider> => {
	const stakeChains = CONFIG.chains
	const crosschainFarmChains = CROSSCHAIN_CONFIG.chains.all.filter(
		(item) =>
			!!(
				CROSSCHAIN_CONFIG.chains.enabled.includes(item.id) &&
				!stakeChains.find((stakeChain) => stakeChain.id === item.id)
			)
	)

	const chainsIds: number[] = []
	const rpcMap: { [chainId: number]: string } = {}
	stakeChains
		.concat(crosschainFarmChains as unknown as ChainConfig[])
		.forEach((chain) => {
			chainsIds.push(chain.id)
			rpcMap[chain.id] = chain.rpc
		})

	const [required, ...optional] = chainsIds

	const provider = await EthereumProvider.init({
		projectId: 'a2a1ecab7189c9e050751b785a3d4ca9',
		chains: [required],
		rpcMap,
		optionalChains: optional,
		showQrModal: true,
		qrModalOptions: {
			themeVariables: {
				'--wcm-z-index': '9999',
			},
		},
	})

	//  Enable session (triggers QR Code modal)
	await provider.enable()

	return new ethers.providers.Web3Provider(provider)
}

export const getProvider = (walletType: WalletType): Promise<Web3Provider> => {
	if (walletType == WalletType.XDEFI) return getXDEFIProvider()
	if (walletType == WalletType.METAMASK) return getMetamaskProvider()
	if (walletType == WalletType.COIN98) return getCoin98Provider()
	if (walletType == WalletType.TRUST) return getTrustProvider()
	if (walletType == WalletType.BITKEEP) return getBitKeepProvider()
	if (walletType == WalletType.OPERA) return getOperaProvider()
	if (walletType == WalletType.SAFE_PAL) return getSafePalProvider()
	if (walletType == WalletType.KUCOIN) return getKuCoinProvider()
	if (walletType == WalletType.IM_TOKEN) return getImTokenProvider()
	if (walletType == WalletType.ONTO) return getOntoProvider()
	if (
		walletType == WalletType.WALLET_CONNECT ||
		walletType == WalletType.BLOCKCHAIN_COM
	)
		return getWalletConnectProvider()

	return Promise.reject(new Error('Unsupported wallet'))
}
